import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import EntryTypeBadge from './EntryTypeBadge'

const PREVIEW_LIMIT = 3

// A compact preview card for one dimension value (e.g. "Figma") showing a
// few of its top entries plus a "view all" link. Hierarchical dimensions
// nest their children as indented sub-cards.
//
// Expected group shape (from Catalog::ViewAggregator):
//   {
//     value:       { id, name, slug, description, parent_id },
//     entry_count: Integer,
//     entries:     [CatalogEntry, ...] (preview),
//     children:    [nested groups]
//   }
export default function DimensionGroupCard({ group, dimensionSlug, depth = 0 }) {
  const [expanded, setExpanded] = useState(true)

  if (!group?.value) return null

  const { value, entry_count: entryCount = 0, entries = [], children = [] } = group
  const previewEntries = entries.slice(0, PREVIEW_LIMIT)
  const viewAllHref = `/catalog?${encodeURIComponent(`dimension[${dimensionSlug}]`)}=${encodeURIComponent(value.slug)}`
  const indentClass = depth > 0 ? 'ml-6 border-l-2 border-slate-100 pl-4' : ''

  return (
    <div className={indentClass}>
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-2 text-left"
              aria-expanded={expanded}
            >
              <svg
                className={`h-3 w-3 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              <h3 className="text-base font-semibold text-slate-900">
                {value.name}
                <span className="ml-2 text-xs font-medium text-slate-500">({entryCount})</span>
              </h3>
            </button>
            {value.description && (
              <p className="mt-1 text-xs text-slate-500 line-clamp-1">{value.description}</p>
            )}
          </div>
        </div>

        {expanded && (
          <>
            {/* Preview entries */}
            {previewEntries.length > 0 && (
              <div className="mt-4 space-y-2">
                {previewEntries.map((entry) => (
                  <Link
                    key={entry.id}
                    to={`/catalog/${entry.id}`}
                    className="block rounded-lg border border-slate-100 bg-slate-50/60 px-4 py-3 transition hover:border-accent-200 hover:bg-white"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {entry.title}
                        </div>
                        {entry.summary && (
                          <p className="mt-0.5 text-xs text-slate-500 truncate">
                            {entry.summary}
                          </p>
                        )}
                      </div>
                      <EntryTypeBadge entryType={entry.entry_type} className="shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* View all link */}
            {entryCount > 0 && (
              <div className="mt-4">
                <Link
                  to={viewAllHref}
                  className="text-xs font-medium text-accent-600 hover:text-accent-500"
                >
                  View all {entryCount} {entryCount === 1 ? 'entry' : 'entries'} →
                </Link>
              </div>
            )}

            {entryCount === 0 && previewEntries.length === 0 && (
              <p className="mt-3 text-xs text-slate-400 italic">No entries yet.</p>
            )}
          </>
        )}
      </div>

      {/* Nested children */}
      {children.length > 0 && expanded && (
        <div className="mt-3 space-y-3">
          {children.map((childGroup) => (
            <DimensionGroupCard
              key={childGroup.value.id}
              group={childGroup}
              dimensionSlug={dimensionSlug}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}
